/**
 * Copyright 2026 Ziz Academy
 */

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { examApi, imageApi, questionApi, categoryApi, examFolderApi } from '../services/api';
import { Exam, Question, Answer, QuestionType, ExamCategory } from '../types';
import { useAuth } from '../context/AuthContext';
import QuestionEditor from '../components/QuestionEditor';
import { SHOW_GEOMETRIC_SHAPES } from '../config/uiFlags';

const EditExam: React.FC = () => {
    const selectionRef = useRef<Range | null>(null);
    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) selectionRef.current = sel.getRangeAt(0);
    };
    const restoreSelection = () => {
        const sel = window.getSelection();
        if (selectionRef.current && sel) {
            sel.removeAllRanges();
            sel.addRange(selectionRef.current);
        }
    };
    const { examId } = useParams<{ examId: string }>();
    const [exam, setExam] = useState<Exam | null>(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [folderOptions, setFolderOptions] = useState<{ id: number | null; label: string }[]>([]);
    const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
    const [durationMinutes, setDurationMinutes] = useState(30);
    const [isActive, setIsActive] = useState(true);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { user } = useAuth();

    // Load folder tree for acting user and flatten to options
    useEffect(() => {
        const loadFolders = async () => {
            try {
                if (!user?.id) { setFolderOptions([]); return; }
                const tree = await examFolderApi.getTree(user.id);
                const flatten = (nodes: any[], level = 0): { id: number | null; label: string }[] => {
                    const res: { id: number | null; label: string }[] = [];
                    for (const n of nodes || []) {
                        res.push({ id: n.id, label: `${'— '.repeat(level)}${n.name}` });
                        if (n.children && n.children.length > 0) {
                            res.push(...flatten(n.children, level + 1));
                        }
                    }
                    return res;
                };
                const opts = flatten(tree);
                setFolderOptions([{ id: null, label: '— None —' }, ...opts]);
            } catch (e) {
                console.error('Failed to load folders', e);
                setFolderOptions([{ id: null, label: '— None —' }]);
            }
        };
        loadFolders();
    }, [user?.id]);

    // Select-from-bank modal state (institution/global scope)
    const [selectOpen, setSelectOpen] = useState(false);
    const [pickSearch, setPickSearch] = useState('');
    const [pickSubject, setPickSubject] = useState('');
    const [pickClass, setPickClass] = useState('');
    const [pickPage, setPickPage] = useState(0);
    const [pickSize] = useState(10);
    const [pickTotalPages, setPickTotalPages] = useState(0);
    const [pickTotal, setPickTotal] = useState(0);
    const [pickList, setPickList] = useState<Question[]>([]);
    const [pickLoading, setPickLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [importing, setImporting] = useState(false);

    // Subject categories and classes for the shared QuestionEditor
    const [categories, setCategories] = useState<ExamCategory[]>([]);
    const defaultClasses = ['Open','1st Class','2nd Class','3rd Class','4th Class','5th Class','6th Class','7th Class','8th Class','9th Class','10th Class','11th Class','12th Class'];
    const [classes, setClasses] = useState<string[]>(defaultClasses);

    useEffect(() => {
        const loadCats = async () => {
            try {
                const list = await categoryApi.list();
                setCategories(list);
            } catch (e) {
                console.error('Failed to load categories', e);
            }
        };
        loadCats();
    }, []);

    // --- Shapes drag state & helpers ---
    type DragState = {
        qIndex: number;
        shapeIndex: number;
        type: string;
        startX: number;
        startY: number;
        orig: any;
        origPoints?: { x: number; y: number }[]; // for polygons
    } | null;

    const dragRef = useRef<DragState>(null);

    const svgEventToViewBox = (e: any) => {
        // Ensure we resolve the actual <svg> element even if the event originates from a child shape
        const ct = e.currentTarget as Element | null;
        const tg = e.target as Element | null;
        const svg: SVGSVGElement | null =
            (ct instanceof (window as any).SVGSVGElement ? (ct as SVGSVGElement) : (ct as any)?.ownerSVGElement) ||
            (tg instanceof (window as any).SVGSVGElement ? (tg as SVGSVGElement) : (tg as any)?.ownerSVGElement) ||
            (ct && (ct as any).closest ? ((ct as any).closest('svg') as SVGSVGElement) : null) ||
            (tg && (tg as any).closest ? ((tg as any).closest('svg') as SVGSVGElement) : null);

        if (!svg) {
            // Fallback safe values
            const clientX = e.clientX ?? (e.touches?.[0]?.clientX || 0);
            const clientY = e.clientY ?? (e.touches?.[0]?.clientY || 0);
            return { x: clientX, y: clientY };
        }

        const rect = svg.getBoundingClientRect();
        const vb = (svg as any).viewBox?.baseVal ?? { x: 0, y: 0, width: rect.width || 1, height: rect.height || 1 };
        const clientX = e.clientX ?? (e.touches?.[0]?.clientX || 0);
        const clientY = e.clientY ?? (e.touches?.[0]?.clientY || 0);
        const x = ((clientX - rect.left) / rect.width) * vb.width + vb.x;
        const y = ((clientY - rect.top) / rect.height) * vb.height + vb.y;
        return { x, y };
    };

    const startShapeDrag = (e: any, qIndex: number, shapeIndex: number, shape: any) => {
        e.preventDefault();
        e.stopPropagation();
        const { x, y } = svgEventToViewBox(e);
        let origPoints: { x: number; y: number }[] | undefined;
        if (shape.type === 'trapezium' && typeof shape.points === 'string') {
            origPoints = shape.points.split(/\s+/).map((p: string) => {
                const [px, py] = p.split(',').map((n: string) => parseFloat(n));
                return { x: px, y: py };
            });
        }
        dragRef.current = {
            qIndex,
            shapeIndex,
            type: shape.type,
            startX: x,
            startY: y,
            orig: { ...shape },
            origPoints,
        };
    };

    const handleShapeDragMove = (e: any, qIndex: number) => {
        if (!dragRef.current || dragRef.current.qIndex !== qIndex) return;
        const { x, y } = svgEventToViewBox(e);
        const dx = x - dragRef.current.startX;
        const dy = y - dragRef.current.startY;
        const shapes = JSON.parse(questions[qIndex].shapesJson || '[]');
        const idx = dragRef.current.shapeIndex;
        const s = { ...shapes[idx] };
        switch (dragRef.current.type) {
            case 'circle':
                s.cx = (dragRef.current.orig.cx || 0) + dx;
                s.cy = (dragRef.current.orig.cy || 0) + dy;
                break;
            case 'rect':
                s.x = (dragRef.current.orig.x || 0) + dx;
                s.y = (dragRef.current.orig.y || 0) + dy;
                break;
            case 'square':
                s.x = (dragRef.current.orig.x || 0) + dx;
                s.y = (dragRef.current.orig.y || 0) + dy;
                break;
            case 'trapezium':
                if (dragRef.current.origPoints) {
                    const moved = dragRef.current.origPoints.map(pt => ({ x: pt.x + dx, y: pt.y + dy }));
                    s.points = moved.map(pt => `${pt.x},${pt.y}`).join(' ');
                }
                break;
            default:
                break;
        }
        shapes[idx] = s;
        updateQuestion(qIndex, 'shapesJson', JSON.stringify(shapes));
    };

    const endShapeDrag = () => {
        dragRef.current = null;
    };

    // -------- Select-from-bank logic --------
    const fetchPick = async (targetPage: number = pickPage) => {
        if (!user?.id) return;
        try {
            setPickLoading(true);
            const res = await questionApi.listForInstitutionPaged(user.id, {
                page: targetPage,
                size: pickSize,
                search: pickSearch || undefined,
                subject: pickSubject || undefined,
                classLevel: pickClass || undefined,
            });
            setPickList(res.content || []);
            setPickTotal(res.totalElements || 0);
            setPickTotalPages(res.totalPages || 0);
            setPickPage(res.page ?? targetPage);
        } catch (e) {
            console.error('Failed to load institution questions', e);
            setPickList([]);
            setPickTotal(0);
            setPickTotalPages(0);
        } finally {
            setPickLoading(false);
        }
    };

    useEffect(() => {
        if (!selectOpen) return;
        fetchPick(pickPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectOpen, user?.id, pickPage, pickSearch, pickSubject, pickClass]);

    const toggleSelect = (id: number, checked: boolean) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
        });
    };

    const importSelected = async () => {
        try {
            setImporting(true);
            const toImport = pickList.filter(q => q.id && selectedIds.has(q.id));
            if (toImport.length === 0) {
                setSelectOpen(false);
                setSelectedIds(new Set());
                return;
            }
            const cloned: Question[] = toImport.map((q) => ({
                id: undefined,
                questionText: q.questionText || '',
                questionRichText: q.questionRichText || '',
                shapesJson: q.shapesJson || '[]',
                imagePath: q.imagePath || '',
                imageAsPrimary: !!q.imageAsPrimary,
                subject: q.subject,
                classLevel: q.classLevel,
                complexity: q.complexity as any,
                tags: q.tags ? [...q.tags] : [],
                questionType: q.questionType,
                marks: q.marks,
                answers: (q.answers || []).map(a => ({ id: undefined, answerText: a.answerText, isCorrect: !!a.isCorrect })),
            }));
            setQuestions(prev => [...prev, ...cloned]);
            setSelectOpen(false);
            setSelectedIds(new Set());
        } catch (e) {
            console.error('Failed to import selected questions', e);
        } finally {
            setImporting(false);
        }
    };

    useEffect(() => {
        const fetchExam = async () => {
            try {
                const data = await examApi.getById(Number(examId));
                setExam(data);
                setTitle(data.title);
                setDescription(data.description ?? '');
                setDurationMinutes(data.durationMinutes ?? 30);
                setIsActive(data.isActive ?? true);
                setQuestions(data.questions ?? []);
                setSelectedFolderId(data.folderId ?? null);
            } catch (error) {
                console.error('Error fetching exam:', error);
                setError('Failed to load exam.');
            } finally {
                setLoading(false);
            }
        };

        fetchExam();
    }, [examId]);

    const totalMarks = questions.reduce((sum, q) => sum + (q.marks || 0), 0);

    const addQuestion = (type: QuestionType) => {
        const newQuestion: Question = {
            questionText: '',
            questionRichText: '',
            shapesJson: '[]',
            questionType: type,
            marks: 5,
            answers: type === 'MCQ'
                ? [
                    { answerText: '', isCorrect: true },
                    { answerText: '', isCorrect: false },
                    { answerText: '', isCorrect: false },
                    { answerText: '', isCorrect: false },
                ]
                : [],
        };
        setQuestions([...questions, newQuestion]);
    };

    const updateQuestion = (index: number, field: string, value: string | number | boolean) => {
        const updated = [...questions];
        updated[index] = { ...updated[index], [field]: value };
        setQuestions(updated);
    };

    const updateAnswer = (qIndex: number, aIndex: number, field: string, value: string | boolean) => {
        const updated = [...questions];
        const answers = [...(updated[qIndex].answers || [])];

        if (field === 'isCorrect' && value === true) { // Only one correct answer allowed for MCQs
            answers.forEach((a, i) => {
                answers[i] = { ...a, isCorrect: i === aIndex };
            });
        } else {
            answers[aIndex] = { ...answers[aIndex], [field]: value };
        }

        updated[qIndex] = { ...updated[qIndex], answers };
        setQuestions(updated);
    };

    const removeQuestion = (index: number) => {
        setQuestions(questions.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (questions.length === 0) {
            setError('Please add at least one question');
            return;
        }

        // Validate each question has either plain/rich text OR a primary image
        for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            const plain = (q.questionText || '').trim();
            const rich = (q.questionRichText || '')
                .replace(/<(script|style)[^>]*>[\s\S]*?<\/(script|style)>/gi, ' ')
                .replace(/<[^>]+>/g, ' ')
                .replace(/&nbsp;/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
            const hasPrimaryImage = !!q.imagePath && q.imagePath.trim().length > 0 && !!q.imageAsPrimary;
            if (!plain && !rich && !hasPrimaryImage) {
                setError(`Question ${i + 1}: Provide text/rich text or mark an uploaded image as primary.`);
                return;
            }
        }

        // Validate MCQ questions have 4 answers with one
        for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            if (q.questionType === 'MCQ') {
                if (!q.answers || q.answers.length < 4) {
                    setError(`Question ${i + 1}: MCQ must have at least 4 answers`);
                    return;
                }
                const correctCount = q.answers.filter((a: Answer) => a.isCorrect).length;
                if (correctCount !== 1) {
                    setError(`Question ${i + 1}: MCQ must have exactly one correct answer`);
                    return;
                }
            }
        }

        setSaving(true);
        try {
            await examApi.update(Number(examId), {
                ...exam,
                title,
                description,
                durationMinutes,
                totalMarks,
                isActive: true,
                folderId: selectedFolderId ?? undefined,
                questions,
            });
            navigate('/');
        } catch (err) {
            setError('Failed to update exam. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // Save current exam as Draft and stay on the same page
    const handleSaveDraft = async () => {
        setError('');
        if (!title.trim()) {
            setError('Please provide a title to save a draft.');
            return;
        }
        setSaving(true);
        try {
            const saved = await examApi.updateDraft(Number(examId), {
                title,
                description,
                durationMinutes,
                totalMarks,
                folderId: selectedFolderId ?? undefined,
                questions,
            }, user!.id);
            // Update local state with saved draft (remain on screen)
            setExam(saved);
            setQuestions(saved.questions || []);
        } catch (err) {
            console.error('Failed to save draft', err);
            setError('Failed to save draft. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // Save current exam as Draft and navigate away
    const handleSaveDraftAndClose = async () => {
        setError('');
        if (!title.trim()) {
            setError('Please provide a title before saving.');
            return;
        }
        setSaving(true);
        try {
            // Save current changes and close the editor
            await examApi.update(Number(examId), {
                ...exam,
                title,
                description,
                durationMinutes,
                totalMarks,
                isActive, // preserve current status
                folderId: selectedFolderId ?? undefined,
                questions,
            });
            navigate('/');
        } catch (err) {
            console.error('Failed to save changes', err);
            setError('Failed to save changes. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="loading" data-testid="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!exam) {
        return (
            <div className="empty-state">
                <div className="empty-state-emoji">😕</div>
                <h3 className="empty-state-title">Exam not found</h3>
            </div>
        );
    }

    return (
        <div data-testid="edit-exam">
            <div className="card" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                    ✏️ Edit Exam
                </h1>
                <p style={{ opacity: 0.9 }}>
                    Update your exam questions and settings!
                </p>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <form onSubmit={handleSubmit}>
                <div className="card">
                    <h3 style={{ marginBottom: '1.5rem' }}>📋 Exam Details</h3>

                    <div className="form-group">
                        <label className="form-label" htmlFor="title">
                            📚 Exam Title
                        </label>
                        <input
                            type="text"
                            id="title"
                            className="form-input"
                            placeholder="e.g., Fun Math Quiz 🧮"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                            data-testid="exam-title"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label" htmlFor="description">
                            📝 Description
                        </label>
                        <textarea
                            id="description"
                            className="form-input"
                            placeholder="Describe what this exam is about..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            data-testid="exam-description"
                        />
                    </div>

                    <div className="grid grid-3">
                        <div className="form-group">
                            <label className="form-label" htmlFor="folder">
                                🗂 Folder
                            </label>
                            <select
                                id="folder"
                                className="form-input"
                                value={String(selectedFolderId)}
                                onChange={(e) => setSelectedFolderId(e.target.value === 'null' ? null : Number(e.target.value))}
                            >
                                {folderOptions.map(opt => (
                                    <option key={String(opt.id)} value={String(opt.id)}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label" htmlFor="duration">
                                ⏱️ Duration (minutes)
                            </label>
                            <input
                                type="number"
                                id="duration"
                                className="form-input"
                                min="1"
                                value={durationMinutes}
                                onChange={(e) => setDurationMinutes(Number(e.target.value))}
                                required
                                data-testid="exam-duration"
                            />
                        </div>

                        <div className="form-group">
                             <label className="form-label" htmlFor="status">
                                🔄 Status
                            </label>
                            <select
                                id="status"
                                className="form-input"
                                value={isActive ? 'active' : 'inactive'}
                                onChange={(e) => setIsActive(e.target.value === 'active')}
                                data-testid="exam-status"
                            >
                                <option value="active">✅ Active</option>
                                <option value="inactive">❌ Inactive</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ padding: '1rem', backgroundColor: '#f0f4f8', borderRadius: '15px' }}>
                        <strong>📊 Total Marks: {totalMarks}</strong>
                    </div>
                </div>

                {/* Questions */}
                <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    ❓ Questions ({questions.length})
                </h3>

                {questions.map((question, qIndex) => (
                    <div
                        key={qIndex}
                        className={`question-card ${question.questionType.toLowerCase()}`}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <div>
                                <span className="question-number">{qIndex + 1}</span>
                                <span className="badge badge-info" style={{ marginLeft: '0.5rem' }}>
                                    {question.questionType === 'MCQ' ? '🔘 MCQ' : '✍ Subjective'}
                                </span>
                            </div>
                            <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => removeQuestion(qIndex)}
                            >
                                🗑️ Remove
                            </button>
                        </div>

                        {/* Shared Question Editor */}
                        <QuestionEditor
                            question={question}
                            onChange={(patch) => {
                                const updated = { ...question, ...patch };
                                const list = [...questions];
                                list[qIndex] = updated;
                                setQuestions(list);
                            }}
                            showSubjectClass={false}
                            showComplexityTags={true}
                            showShapesEditor={SHOW_GEOMETRIC_SHAPES}
                            categories={categories}
                            classes={classes}
                            defaultSubject={exam?.categoryName}
                            defaultClassLevel={exam?.classLevel}
                        />
                    </div>
                ))}

                {/* Add Question Buttons */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
                    <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => addQuestion('MCQ')}
                        data-testid="add-mcq"
                    >
                        ➕ Add MCQ
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => addQuestion('SUBJECTIVE')}
                        data-testid="add-subjective"
                    >
                        ➕ Add Subjective
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setSelectOpen(true)}
                        data-testid="add-from-bank"
                    >
                        📚 Add from Bank
                    </button>
                </div>

                {/* Select From Bank Modal */}
                {selectOpen && (
                    <div className="modal" style={{ display: 'block', width: '95vw', maxWidth: '100%', maxHeight: 'none', overflowY: 'visible' }}>
                        <div className="modal-content" style={{ width: '95vw', maxWidth: '100%', maxHeight: 'none', overflowY: 'visible' }}>
                            <div className="modal-header">
                                <h3>Select Questions from {user?.role === 'SUPER_ADMIN' ? 'All Institutions' : 'Your Institution'}</h3>
                                <button className="close" onClick={() => setSelectOpen(false)}>×</button>
                            </div>
                            <div className="modal-body" style={{ maxHeight: 'none', overflowY: 'visible' }}>
                                {/* Filters */}
                                <div className="card" style={{ marginBottom: '0.75rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px 180px auto', gap: '0.5rem' }}>
                                        <input
                                            type="text"
                                            className="form-input"
                                            placeholder="Search text or tag"
                                            value={pickSearch}
                                            onChange={(e) => { setPickSearch(e.target.value); setPickPage(0); }}
                                        />
                                        <input
                                            type="text"
                                            className="form-input"
                                            placeholder="Subject"
                                            value={pickSubject}
                                            onChange={(e) => { setPickSubject(e.target.value); setPickPage(0); }}
                                        />
                                        <input
                                            type="text"
                                            className="form-input"
                                            placeholder="Class"
                                            value={pickClass}
                                            onChange={(e) => { setPickClass(e.target.value); setPickPage(0); }}
                                        />
                                        <div>
                                            <button type="button" className="btn btn-outline" onClick={() => { setPickSearch(''); setPickSubject(''); setPickClass(''); setPickPage(0); }}>Reset</button>
                                        </div>
                                    </div>
                                </div>

                                {/* Table */}
                                <div className="table-container" style={{ overflowY: 'visible', maxHeight: 'none' }}>
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>Subject</th>
                                                <th>Class</th>
                                                <th>Type</th>
                                                <th>Marks</th>
                                                <th>Tags</th>
                                                <th>Preview</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pickLoading ? (
                                                <tr><td colSpan={7}><div className="spinner" /></td></tr>
                                            ) : pickList.length === 0 ? (
                                                <tr><td colSpan={7}>No questions found</td></tr>
                                            ) : pickList.map((q) => (
                                                <tr key={q.id}>
                                                    <td>
                                                        <input type="checkbox" checked={!!selectedIds.has(q.id!)} onChange={(e) => toggleSelect(q.id!, e.target.checked)} />
                                                    </td>
                                                    <td>{q.subject || '-'}</td>
                                                    <td>{q.classLevel || '-'}</td>
                                                    <td>{q.questionType}</td>
                                                    <td>{q.marks}</td>
                                                    <td>{(q.tags || []).slice(0,3).join(', ')}{(q.tags||[]).length>3?` +${(q.tags||[]).length-3}`:''}</td>
                                                    <td style={{ maxWidth: 320 }}>
                                                        {q.imageAsPrimary && q.imagePath ? (
                                                            <img src={q.imagePath} alt="q" style={{ maxHeight: 60 }} />
                                                        ) : (
                                                            <span title={(q.questionText || '').substring(0, 200)}>
                                                                {(q.questionText || '').substring(0, 60)}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {/* Pagination */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                                        <div>Showing {pickList.length} of {pickTotal} results</div>
                                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                            <button className="btn btn-outline btn-sm" disabled={pickPage<=0} onClick={() => setPickPage(p => Math.max(0, p-1))}>Prev</button>
                                            {Array.from({ length: pickTotalPages }).map((_, i) => (
                                                <button key={i} className={"btn btn-sm "+(i===pickPage? 'btn-primary':'btn-outline')} disabled={i===pickPage} onClick={() => setPickPage(i)}>{i+1}</button>
                                            ))}
                                            <button className="btn btn-outline btn-sm" disabled={pickPage>=pickTotalPages-1} onClick={() => setPickPage(p => Math.min(pickTotalPages-1, p+1))}>Next</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn" onClick={() => setSelectOpen(false)}>Cancel</button>
                                <button className="btn btn-primary" disabled={selectedIds.size===0 || importing} onClick={importSelected}>{importing? 'Adding...' : `Add ${selectedIds.size} Selected`}</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Draft/Submit Buttons */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        className="btn btn-outline btn-lg"
                        disabled={saving}
                        onClick={() => navigate('/')}
                        data-testid="exit"
                    >
                        🚪 Exit
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline btn-lg"
                        disabled={saving}
                        onClick={handleSaveDraft}
                        data-testid="save-draft"
                    >
                        💾 Save as Draft
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary btn-lg"
                        disabled={saving}
                        onClick={handleSaveDraftAndClose}
                        data-testid="save-draft-close"
                    >
                        ✅ Save and Close
                    </button>
                    <button
                        type="submit"
                        className="btn btn-success btn-lg"
                        disabled={saving}
                        data-testid="save-exam"
                    >
                        {saving ? '⌛ Saving...' : '✨ Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditExam;
